import { useState, useEffect } from "react";
import { API } from "../App";
import Layout from "../components/Layout";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { toast } from "sonner";
import { 
  RotateCcw, 
  Search,
  CreditCard,
  CheckCircle,
  XCircle,
  AlertCircle,
  Loader2
} from "lucide-react";

const AdminRefunds = () => {
  const [transactions, setTransactions] = useState([]);
  const [refunds, setRefunds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTxn, setSelectedTxn] = useState(null);
  const [isRefundOpen, setIsRefundOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [processing, setProcessing] = useState(false);
  
  const [refundForm, setRefundForm] = useState({
    amount: "",
    reason: ""
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [txnRes, refundsRes] = await Promise.all([
        axios.get(`${API}/admin/transactions`, { withCredentials: true }),
        axios.get(`${API}/admin/refunds`, { withCredentials: true })
      ]);
      setTransactions(txnRes.data.filter(t => t.payment_status === 'paid'));
      setRefunds(refundsRes.data);
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenRefund = (txn) => {
    setSelectedTxn(txn);
    setRefundForm({ amount: txn.amount.toString(), reason: "" });
    setIsRefundOpen(true);
  };

  const handleProcessRefund = async () => {
    if (!refundForm.reason.trim()) {
      toast.error("Please provide a reason for the refund");
      return;
    }

    setProcessing(true);
    try {
      const refundAmount = refundForm.amount ? parseFloat(refundForm.amount) : null;
      
      await axios.post(`${API}/admin/refunds`, {
        transaction_id: selectedTxn.transaction_id,
        amount: refundAmount,
        reason: refundForm.reason
      }, { withCredentials: true });
      
      toast.success("Refund processed successfully!");
      setIsRefundOpen(false);
      setSelectedTxn(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to process refund");
    } finally {
      setProcessing(false);
    }
  };

  const getStatusBadge = (txn) => {
    if (txn.refund_status === 'refunded') {
      return <Badge className="bg-red-100 text-red-700">Fully Refunded</Badge>;
    }
    if (txn.refund_status === 'partial_refund') {
      return <Badge className="bg-orange-100 text-orange-700">Partial Refund</Badge>;
    }
    return <Badge className="bg-emerald-100 text-emerald-700">Paid</Badge>;
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredTransactions = transactions.filter(txn => 
    txn.user_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    txn.user_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    txn.transaction_id?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalRefunded = refunds.reduce((acc, r) => acc + (r.refund_amount || 0), 0);

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-700"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-8 animate-fade-in" data-testid="admin-refunds">
        {/* Header */}
        <div>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-stone-900" style={{ fontFamily: 'Outfit' }}>
            Refunds
          </h1>
          <p className="text-stone-600 mt-1">Process refunds for paid transactions</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="bg-white rounded-xl border border-stone-100">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 bg-violet-100 rounded-xl flex items-center justify-center">
                <CreditCard className="h-6 w-6 text-violet-700" />
              </div>
              <div>
                <p className="text-sm text-stone-500">Paid Transactions</p>
                <p className="text-2xl font-bold text-stone-900">{transactions.length}</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white rounded-xl border border-stone-100">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
                <RotateCcw className="h-6 w-6 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-stone-500">Total Refunds</p>
                <p className="text-2xl font-bold text-stone-900">{refunds.length}</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white rounded-xl border border-stone-100">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center">
                <AlertCircle className="h-6 w-6 text-red-600" />
              </div>
              <div>
                <p className="text-sm text-stone-500">Amount Refunded</p>
                <p className="text-2xl font-bold text-stone-900">£{totalRefunded.toFixed(2)}</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-stone-400" />
          <Input
            placeholder="Search by email, name, or transaction ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 h-12 rounded-xl"
            data-testid="search-transactions"
          />
        </div>

        {/* Transactions List */}
        <Card className="bg-white rounded-xl border border-stone-100">
          <CardHeader>
            <CardTitle style={{ fontFamily: 'Outfit' }}>Paid Transactions</CardTitle>
            <CardDescription>Click on a transaction to process a refund</CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            {filteredTransactions.length === 0 ? (
              <div className="p-12 text-center">
                <CreditCard className="h-16 w-16 text-stone-300 mx-auto mb-4" />
                <h3 className="text-xl font-medium text-stone-900 mb-2">No transactions found</h3>
                <p className="text-stone-500">Paid transactions will appear here</p>
              </div>
            ) : (
              <div className="divide-y divide-stone-100">
                {filteredTransactions.map((txn) => (
                  <div 
                    key={txn.transaction_id}
                    className="p-4 flex items-center gap-4 hover:bg-stone-50 transition-colors cursor-pointer"
                    onClick={() => !txn.refund_status && handleOpenRefund(txn)}
                    data-testid={`txn-row-${txn.transaction_id}`}
                  >
                    <div className="w-10 h-10 bg-violet-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <CreditCard className="h-5 w-5 text-violet-700" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-stone-900">{txn.user_name}</p>
                      <p className="text-sm text-stone-500">{txn.user_email}</p>
                      <p className="text-xs text-stone-400 font-mono mt-1">{txn.transaction_id}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-stone-900">£{txn.amount.toFixed(2)}</p>
                      <p className="text-xs text-stone-500">{formatDate(txn.created_at)}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      {getStatusBadge(txn)}
                      {!txn.refund_status && (
                        <Button 
                          variant="outline" 
                          size="sm"
                          className="text-orange-600 border-orange-200 hover:bg-orange-50"
                          data-testid={`refund-btn-${txn.transaction_id}`}
                        >
                          <RotateCcw className="h-4 w-4 mr-1" />
                          Refund
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Refund Dialog */}
        <Dialog open={isRefundOpen} onOpenChange={setIsRefundOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Outfit' }}>Process Refund</DialogTitle>
            </DialogHeader>
            {selectedTxn && (
              <div className="space-y-4 pt-4">
                <div className="p-4 bg-stone-50 rounded-xl">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-medium text-stone-900">{selectedTxn.user_name}</p>
                      <p className="text-sm text-stone-500">{selectedTxn.user_email}</p>
                    </div>
                    <p className="text-lg font-bold text-stone-900">
                      £{selectedTxn.amount.toFixed(2)}
                    </p>
                  </div>
                </div>

                <div>
                  <Label>Refund Amount (GBP)</Label>
                  <Input
                    type="number"
                    min="0.01"
                    max={selectedTxn.amount}
                    step="0.01"
                    value={refundForm.amount}
                    onChange={(e) => setRefundForm({ ...refundForm, amount: e.target.value })}
                    placeholder={`Max: £${selectedTxn.amount.toFixed(2)}`}
                    className="mt-1"
                    data-testid="refund-amount-input"
                  />
                  <p className="text-xs text-stone-500 mt-1">
                    Leave as full amount for complete refund, or enter partial amount
                  </p>
                </div>

                <div>
                  <Label>Reason for Refund *</Label>
                  <Textarea
                    value={refundForm.reason}
                    onChange={(e) => setRefundForm({ ...refundForm, reason: e.target.value })}
                    placeholder="Please provide a reason for this refund..."
                    rows={3}
                    className="mt-1"
                    data-testid="refund-reason-input"
                  />
                </div>

                <div className="flex gap-3">
                  <Button 
                    variant="outline" 
                    className="flex-1"
                    onClick={() => setIsRefundOpen(false)}
                  >
                    Cancel
                  </Button>
                  <Button 
                    className="flex-1 bg-orange-600 hover:bg-orange-700"
                    onClick={handleProcessRefund}
                    disabled={processing}
                    data-testid="confirm-refund-btn"
                  >
                    {processing ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <RotateCcw className="h-4 w-4 mr-2" />
                        Process Refund
                      </>
                    )}
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default AdminRefunds;
